
import React from "react";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { Conversation } from "../types";
import { ConversationActions } from "./ConversationActions";
import { Mail, Clock, Users, Star } from "lucide-react";

interface ConversationCardProps {
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
}

export const ConversationCard = ({ conversation, isSelected, onClick }: ConversationCardProps) => {
  const getStatusBadge = () => {
    switch (conversation.status) {
      case 'archived':
        return <Badge variant="secondary" className="text-xs">Archived</Badge>;
      case 'closed':
        return <Badge variant="outline" className="text-xs">Closed</Badge>;
      default:
        return null;
    }
  };

  const getPriorityColor = () => {
    if (!conversation.overallPriorityScore) return "";
    if (conversation.overallPriorityScore >= 8) return "border-l-red-500";
    if (conversation.overallPriorityScore >= 6) return "border-l-orange-500";
    if (conversation.overallPriorityScore >= 4) return "border-l-yellow-500";
    return "border-l-green-500";
  };

  return (
    <div
      className={`group relative p-4 border-b cursor-pointer transition-all duration-200 border-l-4 ${getPriorityColor()} ${
        isSelected 
          ? "bg-blue-50 border-blue-200 shadow-sm" 
          : "hover:bg-gray-50 border-transparent hover:border-gray-200"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {/* Avatar */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${
            conversation.unread ? "bg-blue-600" : "bg-gray-500"
          }`}>
            {conversation.initials}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-center justify-between mb-1">
              <h3 className={`text-sm font-medium truncate ${
                conversation.unread ? "text-gray-900" : "text-gray-700"
              }`}>
                {conversation.name}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                {conversation.unread && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
                {getStatusBadge()}
              </div>
            </div>

            {/* Participants */}
            {conversation.participantEmails && conversation.participantEmails.length > 0 && (
              <div className="flex items-center gap-1 mb-2">
                <Users className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500 truncate">
                  {conversation.participantEmails.slice(0, 2).join(', ')}
                  {conversation.participantEmails.length > 2 && (
                    <span className="text-gray-400"> +{conversation.participantEmails.length - 2} more</span>
                  )}
                </span>
              </div>
            )}

            {/* Last message preview */}
            <p className="text-xs text-gray-600 line-clamp-2 mb-2">
              {conversation.lastMessage}
            </p>

            {/* Footer row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Clock className="h-3 w-3" />
                <span>{conversation.timestamp}</span>
              </div>
              
              <div className="flex items-center gap-2">
                {conversation.overallPriorityScore && (
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-500" />
                    <span className="text-xs text-gray-500">{conversation.overallPriorityScore}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions - only show on hover */}
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <ConversationActions 
            conversationId={conversation.id}
            currentStatus={conversation.status || 'open'}
          />
        </div>
      </div>
    </div>
  );
};
